import extractIdFromGid from './shopify/extract-id-from-gid';

/**
 * Normalize Shopify GIDs / numeric IDs for set membership checks.
 * Gatsby and Admin API may return either form.
 */
export const normalizeShopifyId = (id) => {
  if (!id) {
    return null;
  }
  const asString = String(id);
  return extractIdFromGid(asString) ?? asString;
};

const toNormalizedIdSet = (ids = []) => (
  new Set(ids.map(normalizeShopifyId).filter(Boolean))
);

export const isProductEligibleForStorewideSale = (promotion, product) => {
  if (!promotion || !product) {
    return false;
  }

  if (promotion.appliesToAll) {
    return true;
  }

  const productId = normalizeShopifyId(product.shopifyId);
  if (productId && toNormalizedIdSet(promotion.productIds).has(productId)) {
    return true;
  }

  const collectionIdSet = toNormalizedIdSet(promotion.collectionIds);
  if (collectionIdSet.size === 0) {
    return false;
  }

  return (product.collections ?? []).some((collection) => (
    collectionIdSet.has(normalizeShopifyId(collection.shopifyId ?? collection.id))
  ));
};

/**
 * Variant-level eligibility. When the discount lists specific variants, only those
 * variants get the sale price. Otherwise fall back to product/collection targeting.
 */
export const isVariantEligibleForStorewideSale = (promotion, product, variant) => {
  if (!promotion || !variant) {
    return false;
  }

  const variantIdSet = toNormalizedIdSet(promotion.variantIds);
  if (variantIdSet.size > 0) {
    return variantIdSet.has(normalizeShopifyId(variant.shopifyId));
  }

  return isProductEligibleForStorewideSale(promotion, product);
};

export const roundMoney = (amount) => (
  Math.round((amount + Number.EPSILON) * 100) / 100
);

/**
 * Format a percentage-off figure for display: rounded to one decimal place,
 * with any trailing ".0" trimmed (e.g. 12.525 -> 12.5, 10 -> 10).
 */
export const formatPercentOff = (percent) => {
  if (!Number.isFinite(percent)) {
    return 0;
  }
  return Math.round(percent * 10) / 10;
};

/**
 * Compute the displayed sale unit price from a list price and active promotion.
 * Returns null when the promo cannot produce a valid sale price.
 */
export const computeStorewideSalePrice = (listPrice, promotion) => {
  const price = parseFloat(listPrice);
  if (!Number.isFinite(price) || price <= 0 || !promotion) {
    return null;
  }

  let salePrice = null;
  if (promotion.percentage != null) {
    salePrice = price * (1 - promotion.percentage);
  } else if (promotion.amountOff != null) {
    salePrice = price - promotion.amountOff;
  }

  if (salePrice == null || !Number.isFinite(salePrice) || salePrice <= 0 || salePrice >= price) {
    return null;
  }

  return roundMoney(salePrice);
};

export const getStorewideSalePercentOff = (promotion) => {
  if (promotion?.percentage == null) {
    return null;
  }
  return formatPercentOff(promotion.percentage * 100);
};

export const getStorewideSalePromoState = ({ isError, isSuccess, data }) => {
  if (isError) {
    return 'unknown';
  }

  if (!isSuccess) {
    return 'unknown';
  }

  return data?.promotion ? 'active' : 'inactive';
};
